import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import { SPECIAL_QUOTA_LINK } from '../../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const POINTS = [1, 2, 3, 4];

// One-off translucent treatments for this dark card. Kept local rather than in
// REDESIGN_COLORS: they only read correctly on top of `dark`, and the design
// uses a different on-dark eyebrow here than it does on the contexts card.
const ON_DARK = {
    eyebrowBorder: 'rgba(255, 255, 255, 0.16)',
    eyebrowBackground: 'rgba(16, 169, 179, 0.25)',
    ctaBorder: 'rgba(255, 255, 255, 0.28)',
    ctaHoverBackground: 'rgba(16, 169, 179, 0.18)',
    rowBorder: 'rgba(255, 255, 255, 0.13)',
    rowBackground: 'rgba(255, 255, 255, 0.06)',
};

const useStyles = makeStyles(() => ({
    card: {
        boxSizing: 'border-box',
        position: 'relative',
        // Clips the ring that bleeds off the bottom-right corner.
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: '44px',
        padding: '60px',
        borderRadius: '30px',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.dark,
    },
    copy: {
        position: 'relative',
        zIndex: 2,
    },
    eyebrow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '20px',
        padding: '8px 13px',
        border: `1px solid ${ON_DARK.eyebrowBorder}`,
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        background: ON_DARK.eyebrowBackground,
        fontSize: '0.76rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
    },
    dot: {
        flexShrink: 0,
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: REDESIGN_COLORS.accent,
    },
    title: {
        margin: '0 0 18px',
        maxWidth: '640px',
        fontSize: 'clamp(2rem, 4vw, 3.2rem)',
        lineHeight: 1.08,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.white,
    },
    lead: {
        margin: '0 0 26px',
        maxWidth: '620px',
        color: REDESIGN_COLORS.bodyOnDark,
        fontSize: '1.04rem',
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50px',
        padding: '0 24px',
        border: `2px solid ${ON_DARK.ctaBorder}`,
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        '&:hover': {
            color: REDESIGN_COLORS.white,
            borderColor: REDESIGN_COLORS.primary,
            background: ON_DARK.ctaHoverBackground,
        },
    },
    points: {
        position: 'relative',
        zIndex: 2,
        display: 'grid',
        alignContent: 'center',
        gap: '12px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    point: {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '14px 16px',
        border: `1px solid ${ON_DARK.rowBorder}`,
        borderRadius: '13px',
        background: ON_DARK.rowBackground,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    tick: {
        flexShrink: 0,
        color: REDESIGN_COLORS.highlight,
    },
    ring: {
        position: 'absolute',
        width: '330px',
        height: '330px',
        right: '-130px',
        bottom: '-150px',
        border: `55px solid ${REDESIGN_COLORS.primary}`,
        borderRadius: '50%',
        opacity: 0.45,
        pointerEvents: 'none',
    },
}));

/**
 * The `#scoli` band: the pitch to schools, on a dark card. Its CTA goes to the
 * existing special-quota page, which is where the design's link pointed too.
 */
const HomeSchools = () => {
    const classes = useStyles();

    return (
        <LandingSection id="scoli" background="none" paddingTop={56} paddingBottom={56}>
            <div className={classes.card}>
                <div className={classes.copy}>
                    <span className={classes.eyebrow}>
                        <span aria-hidden="true" className={classes.dot} />
                        <FormattedMessage id="redesign.home.schools.eyebrow" />
                    </span>
                    <h2 className={classes.title}>
                        <FormattedMessage id="redesign.home.schools.title" />
                    </h2>
                    <p className={classes.lead}>
                        <FormattedMessage id="redesign.home.schools.lead" />
                    </p>
                    <Link to={SPECIAL_QUOTA_LINK} className={classes.cta}>
                        <FormattedMessage id="redesign.home.schools.cta" />
                    </Link>
                </div>

                <ul className={classes.points}>
                    {POINTS.map((n) => (
                        <li key={n} className={classes.point}>
                            <span aria-hidden="true" className={classes.tick}>
                                ✓
                            </span>
                            <FormattedMessage id={`redesign.home.schools.point${n}`} />
                        </li>
                    ))}
                </ul>

                <div aria-hidden="true" className={classes.ring} />
            </div>
        </LandingSection>
    );
};

export default HomeSchools;
